/* =========================================================
   AUTOSTYLE PRODUCT ADD-ONS — V10.1
   - Per-add-on installation cost
   - Exact variant incompatibility
   - Conditional add-ons
   - Separate draggable checkout block
   - Forced multi-item Shopify cart submission
   ========================================================= */
(() => {
  'use strict';

  if (window.AutostyleProductAddonsLoaded) return;
  window.AutostyleProductAddonsLoaded = true;

  const ROOT_SELECTOR = '[data-autostyle-addons]';
  const CHECKOUT_SELECTOR = '[data-autostyle-checkout]';
  const CHECKBOX_SELECTOR = '[data-as-checkbox]';
  const INSTALLATION_PREFERENCE_NAME = 'properties[Installation preference]';
  const INSTALLATION_REQUESTED_VALUE = 'Autostyle installation requested';
  const INSTALLATION_CART_ATTRIBUTE = 'Autostyle installation';

  function money(cents) {
    const value = Number(cents || 0) / 100;

    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value);
  }

  function parseJSONScript(element, selector) {
    const script = element.querySelector(selector);
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.error('Autostyle add-ons: invalid product JSON', error);
      return [];
    }
  }

  function getForm(element) {
    const explicitId = element?.dataset?.productFormId;

    if (explicitId) {
      const explicit = document.getElementById(explicitId);
      if (explicit) return explicit;
    }

    const scope =
      element?.closest?.('product-rerender') ||
      element?.closest?.('[data-section-type="product"]') ||
      element?.closest?.('[id^="MainProduct-"]') ||
      element?.closest?.('section') ||
      document;

    return scope.querySelector('form[action*="/cart/add"]');
  }

  function getQuantity(form) {
    if (!form) return 1;

    const quantity = form.querySelector('[name="quantity"]');
    const parsed = quantity ? parseInt(quantity.value, 10) : 1;

    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  function getSubmitButton(form) {
    if (!form) return null;

    return (
      form.querySelector('button[type="submit"][name="add"]') ||
      form.querySelector('button[data-action="add-to-cart"]') ||
      form.querySelector('.product-form__add-button[type="submit"]') ||
      form.querySelector('button[type="submit"]:not(.shopify-payment-button__button)')
    );
  }

  function checkoutForRoot(root) {
    const formId = String(root?.dataset?.productFormId || '');
    const productId = String(root?.dataset?.productId || '');

    return Array.from(document.querySelectorAll(CHECKOUT_SELECTOR)).find((checkout) => {
      if (formId && String(checkout.dataset.productFormId || '') === formId) return true;

      return (
        productId &&
        String(checkout.dataset.productId || '') === productId &&
        getForm(checkout) === getForm(root)
      );
    }) || null;
  }

  function rootForCheckout(checkout) {
    const formId = String(checkout?.dataset?.productFormId || '');
    const productId = String(checkout?.dataset?.productId || '');

    return Array.from(document.querySelectorAll(ROOT_SELECTOR)).find((root) => {
      if (formId && String(root.dataset.productFormId || '') === formId) return true;

      return (
        productId &&
        String(root.dataset.productId || '') === productId &&
        getForm(root) === getForm(checkout)
      );
    }) || null;
  }

  function hideOriginalProductSubmit(root) {
    const form = getForm(root);
    if (!form) return;

    const originalButton = getSubmitButton(form);

    if (originalButton && !originalButton.matches('[data-as-configured-submit]')) {
      originalButton.dataset.autostyleOriginalSubmit = 'true';
      originalButton.hidden = true;
      originalButton.setAttribute('aria-hidden', 'true');
      originalButton.setAttribute('tabindex', '-1');
    }
  }

  function suppressAcceleratedCheckout(root) {
    const form = getForm(root);
    if (!form) return;

    form.classList.add('autostyle-configured-product-form');
    hideOriginalProductSubmit(root);

    // Hide the complete native Shopify buy-buttons block when the
    // Autostyle configured checkout is active.
    form.querySelectorAll('[data-autostyle-native-buy-buttons]').forEach((element) => {
      element.hidden = true;
      element.style.display = 'none';
      element.setAttribute('aria-hidden', 'true');

      const block = element.closest('.product-info__block-item--buy-buttons');
      if (block) {
        block.hidden = true;
        block.style.display = 'none';
        block.setAttribute('aria-hidden', 'true');
      }
    });

    // The theme outputs a no-JS "Variant" summary row as well as the visual
    // PRO/MAX selector. Hide only that redundant row; keep Price and Quantity.
    const variantBlock = form.querySelector('.product-info__block-item--variant-selector');
    if (variantBlock) {
      variantBlock.querySelectorAll('.product-form__info-item').forEach((row) => {
        const title = row.querySelector('.product-form__info-title');
        const text = String(title?.textContent || '').trim().replace(/:$/, '').toLowerCase();

        if (text === 'variant') {
          row.hidden = true;
          row.style.display = 'none';
          row.setAttribute('aria-hidden', 'true');
        }
      });
    }

    const scope =
      form.closest('product-rerender') ||
      form.closest('[data-section-type="product"]') ||
      form.parentElement ||
      document;

    scope.querySelectorAll(
      '.shopify-payment-button, [data-shopify="payment-button"], shopify-accelerated-checkout'
    ).forEach((element) => {
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
    });
  }

  function selectedAddons(root) {
    return Array.from(root.querySelectorAll(`${CHECKBOX_SELECTOR}:checked`))
      .filter((input) => !input.disabled && input.dataset.available !== 'false')
      .map((input) => ({
        handle: input.dataset.addonHandle || '',
        variantId: Number(input.dataset.variantId),
        price: Number(input.dataset.price || 0),
        title: input.dataset.title || input.dataset.productTitle || 'Add-on',
        productTitle: input.dataset.productTitle || input.dataset.title || 'Add-on',
        installationCost: Math.max(0, Number(input.dataset.installationCost || 0))
      }))
      .filter((item) => Number.isFinite(item.variantId) && item.variantId > 0);
  }

  function currentVariant(root, form) {
    const variants = root._asVariants || [];
    const idField = form ? form.querySelector('[name="id"]') : null;
    const id = idField ? Number(idField.value) : 0;

    return variants.find((variant) => Number(variant.id) === id) || null;
  }

  function incompatibleVariantIds(input) {
    return String(input.dataset.incompatibleVariants || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  }

  function updateCompatibility(root) {
    const form = getForm(root);
    const variant = currentVariant(root, form);
    const currentVariantId = variant ? String(variant.id) : '';

    root.querySelectorAll('[data-as-card]').forEach((card) => {
      const input = card.querySelector(CHECKBOX_SELECTOR);
      if (!input) return;

      const unavailable = input.dataset.available === 'false';
      const incompatible = Boolean(
        currentVariantId &&
        incompatibleVariantIds(input).includes(currentVariantId)
      );

      const message = card.querySelector('[data-as-compatibility-message]');

      card.classList.toggle('autostyle-addon-card--incompatible', incompatible);
      card.setAttribute('aria-disabled', incompatible || unavailable ? 'true' : 'false');

      input.disabled = unavailable || incompatible;

      if (incompatible && input.checked) {
        input.checked = false;
      }

      // Incompatible add-ons should not be offered for the currently
      // selected PRO/MAX variant. Dependency logic may also hide cards,
      // so mark compatibility separately and let updateDependencies()
      // combine both visibility rules.
      card.dataset.asIncompatible = incompatible ? 'true' : 'false';

      if (message) {
        message.textContent = '';
        message.hidden = true;
      }
    });
  }

  function updateDependencies(root) {
    const checkedInputs = Array.from(
      root.querySelectorAll(`${CHECKBOX_SELECTOR}:checked`)
    );

    const selectedIds = new Set(
      checkedInputs
        .map((input) => String(input.dataset.addonId || '').trim())
        .filter(Boolean)
    );

    const selectedHandles = new Set(
      checkedInputs
        .map((input) => String(input.dataset.addonHandle || '').trim())
        .filter(Boolean)
    );

    root.querySelectorAll('[data-as-card]').forEach((card) => {
      const requiredId = String(card.dataset.requiresAddonId || '').trim();
      const requiredHandle = String(card.dataset.requiresAddon || '').trim();

      if (!requiredId && !requiredHandle) {
        card.hidden = card.dataset.asIncompatible === 'true';
        return;
      }

      const dependencySelected =
        (requiredId && selectedIds.has(requiredId)) ||
        (requiredHandle && selectedHandles.has(requiredHandle));

      card.hidden =
        card.dataset.asIncompatible === 'true' || !dependencySelected;

      if (!dependencySelected || card.dataset.asIncompatible === 'true') {
        const checkbox = card.querySelector(CHECKBOX_SELECTOR);

        if (checkbox && checkbox.checked) {
          checkbox.checked = false;
        }
      }
    });

    /*
      Run a second pass so chained dependencies work correctly:
      A selected -> B visible/selected -> C visible.
      If B becomes hidden, C is also hidden and unselected.
    */
    const visibleCheckedInputs = Array.from(
      root.querySelectorAll(`${CHECKBOX_SELECTOR}:checked`)
    ).filter((input) => !input.closest('[data-as-card]')?.hidden);

    const visibleSelectedIds = new Set(
      visibleCheckedInputs
        .map((input) => String(input.dataset.addonId || '').trim())
        .filter(Boolean)
    );

    const visibleSelectedHandles = new Set(
      visibleCheckedInputs
        .map((input) => String(input.dataset.addonHandle || '').trim())
        .filter(Boolean)
    );

    root.querySelectorAll('[data-as-card]').forEach((card) => {
      const requiredId = String(card.dataset.requiresAddonId || '').trim();
      const requiredHandle = String(card.dataset.requiresAddon || '').trim();

      if (!requiredId && !requiredHandle) return;

      const dependencySelected =
        (requiredId && visibleSelectedIds.has(requiredId)) ||
        (requiredHandle && visibleSelectedHandles.has(requiredHandle));

      card.hidden =
        card.dataset.asIncompatible === 'true' || !dependencySelected;

      if (!dependencySelected || card.dataset.asIncompatible === 'true') {
        const checkbox = card.querySelector(CHECKBOX_SELECTOR);
        if (checkbox) checkbox.checked = false;
      }
    });
  }

  function updateAddonSelectionStates(root) {
    root.querySelectorAll('[data-as-card]').forEach((card) => {
      const input = card.querySelector(CHECKBOX_SELECTOR);
      const state = card.querySelector('[data-as-selection-state]');
      if (!input || !state) return;

      const selected =
        input.checked &&
        !input.disabled &&
        input.dataset.available !== 'false' &&
        !card.hidden;

      card.classList.toggle('autostyle-addon-card--selected', selected);

      state.hidden = false;
      state.textContent = selected ? 'Added ✓' : '+ Add';
      state.setAttribute('aria-label', selected ? 'Remove add-on' : 'Add this extra');

      state.style.display = 'inline-flex';
      state.style.alignItems = 'center';
      state.style.justifyContent = 'center';
      state.style.minWidth = '72px';
      state.style.minHeight = '34px';
      state.style.padding = '6px 11px';
      state.style.borderRadius = '8px';
      state.style.fontSize = '13px';
      state.style.fontWeight = '700';
      state.style.lineHeight = '1';
      state.style.whiteSpace = 'nowrap';
      state.style.transition = 'background .18s ease, color .18s ease, border-color .18s ease';
      state.style.background = selected ? '#111111' : '#ffffff';
      state.style.color = selected ? '#ffffff' : '#111111';
      state.style.border = selected ? '1px solid #111111' : '1px solid #cfcfcf';
    });
  }

  function moneyStringToCents(value) {
    const numeric = String(value || '')
      .replace(/[^0-9.,-]/g, '')
      .replace(/,/g, '');

    const parsed = Number.parseFloat(numeric);

    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
  }

  function autostyleInstallationSelected(checkout) {
    const form = getForm(checkout);
    if (!form) return false;

    const selectedPreference = form.querySelector(
      `[name="${INSTALLATION_PREFERENCE_NAME}"]:checked`
    );

    if (!selectedPreference) return false;

    return String(selectedPreference.value || '').trim() ===
      INSTALLATION_REQUESTED_VALUE;
  }

  function syncInstallationEstimate(checkout) {
    if (!checkout) return;

    const form = getForm(checkout);
    const installationRow = checkout.querySelector('[data-as-installation-row]');
    const installationTotal = checkout.querySelector('[data-as-installation-total]');
    const grandTotalRow = checkout.querySelector('[data-as-grand-total-row]');
    const grandTotal = checkout.querySelector('[data-as-grand-total]');
    const installationDeliveryRow = checkout.querySelector(
      '[data-as-installation-delivery-row]'
    );
    const payableToday = checkout.querySelector('[data-as-total]');

    if (!installationRow || !installationTotal) return;

    const estimateProperty = form?.querySelector(
      '[name="properties[Estimated installation price]"]'
    );

    const estimate = String(estimateProperty?.value || '').trim();
    const installationCents = moneyStringToCents(estimate);
    const payableTodayCents = moneyStringToCents(payableToday?.textContent || '');
    const showInstallationTotals = autostyleInstallationSelected(checkout);

    if (showInstallationTotals && estimate && installationCents !== null) {
      installationTotal.textContent = estimate;
      installationRow.hidden = false;
      if (installationDeliveryRow) installationDeliveryRow.hidden = false;

      if (grandTotalRow && grandTotal && payableTodayCents !== null) {
        grandTotal.textContent = money(payableTodayCents + installationCents);
        grandTotalRow.hidden = false;
      }
    } else {
      installationTotal.textContent = '';
      installationRow.hidden = true;
      if (installationDeliveryRow) installationDeliveryRow.hidden = true;

      if (grandTotal) grandTotal.textContent = '';
      if (grandTotalRow) grandTotalRow.hidden = true;
    }
  }


  function hideNativePaymentTerms(root) {
    const form = getForm(root);
    if (!form) return;

    const scope =
      form.closest('product-rerender') ||
      form.closest('[data-section-type="product"]') ||
      form.closest('[id^="MainProduct-"]') ||
      form.closest('section') ||
      form.parentElement ||
      document;

    const nativeTerms =
      form.querySelector('shopify-payment-terms') ||
      scope.querySelector('shopify-payment-terms');

    if (nativeTerms) {
      nativeTerms.style.display = 'none';
      nativeTerms.setAttribute('aria-hidden', 'true');
    }
  }


  function updateMainDisplayedPrice(root, purchaseAmountCents) {
    const checkout = checkoutForRoot(root);
    if (!checkout || checkout.dataset.updateMainProductPrice === 'false') return;

    const amount = Math.max(0, Math.round(Number(purchaseAmountCents) || 0));
    if (!amount) return;

    const form = getForm(root);
    if (!form) return;

    const scope =
      form.closest('product-rerender') ||
      form.closest('[data-section-type="product"]') ||
      form.closest('[id^="MainProduct-"]') ||
      form.closest('section') ||
      form;

    /*
      In this theme the visible "Price:" row is generated by the
      product-variant-selector snippet, so it lives inside the
      variant-selector product-info block rather than product-meta.
    */
    const variantBlock =
      scope.querySelector('.product-info__block-item--variant-selector') ||
      form.querySelector('.product-info__block-item--variant-selector');

    if (!variantBlock) return;

    const priceList = variantBlock.querySelector('.price-list');
    if (!priceList) return;

    /*
      Prefer the active sale/highlight price. If there is no compare-at
      sale, use the normal price but never touch .price--compare.
    */
    const salePrice =
      priceList.querySelector('.price--highlight') ||
      priceList.querySelector('.price:not(.price--compare)');

    if (!salePrice) return;

    salePrice.textContent = money(amount);
    salePrice.dataset.autostyleConfiguredPrice = String(amount);

    /*
      Some versions of the theme add visually-hidden accessibility labels
      inside the price element. Replacing textContent can remove those.
      Restore a useful accessible label on the price itself.
    */
    salePrice.setAttribute(
      'aria-label',
      `Configured price ${money(amount)}`
    );
  }

  function updateFinanceBlock(root, purchaseAmountCents) {
    const checkout = checkoutForRoot(root);
    if (!checkout) return;

    const amount = Math.max(0, Math.round(Number(purchaseAmountCents) || 0));
    if (!amount) return;

    const klarna = checkout.querySelector('[data-as-finance-klarna]');
    const financeTotal = checkout.querySelector('[data-as-finance-total]');

    if (klarna) {
      const showBreakdown = klarna.dataset.showBreakdown !== 'false';
      klarna.textContent = showBreakdown
        ? `3 × ${money(amount / 3)}`
        : 'Available at checkout';
    }

    if (financeTotal) {
      financeTotal.textContent = money(amount);
    }

    hideNativePaymentTerms(root);
  }

  function variantIdForRoot(root) {
    const form = getForm(root);
    if (!form) return '';

    const idField = form.querySelector('[name="id"]');
    return idField ? String(idField.value || '') : '';
  }

  function startVariantWatcher(root) {
    if (!root || root._asVariantWatcher) return;

    root._asLastVariantId = variantIdForRoot(root);

    root._asVariantWatcher = window.setInterval(() => {
      if (!document.documentElement.contains(root)) {
        window.clearInterval(root._asVariantWatcher);
        root._asVariantWatcher = null;
        return;
      }

      const nextId = variantIdForRoot(root);

      if (nextId && nextId !== root._asLastVariantId) {
        root._asLastVariantId = nextId;
        updateRoot(root);
      }
    }, 180);
  }

  function updateCheckout(root) {
    const checkout = checkoutForRoot(root);
    if (!checkout) return;

    const form = getForm(root);
    const quantity = getQuantity(form);
    const matchQuantity = root.dataset.matchMainQuantity !== 'false';
    const addonQuantity = matchQuantity ? quantity : 1;
    const variant = currentVariant(root, form);
    const baseUnitPrice = variant ? Number(variant.price) : Number(root.dataset.basePrice || 0);
    const basePrice = baseUnitPrice * quantity;
    const addons = selectedAddons(root);

    const addonRows = checkout.querySelector('[data-as-summary-addons]');
    const mainPrice = checkout.querySelector('[data-as-main-price]');
    const total = checkout.querySelector('[data-as-total]');
    const button = checkout.querySelector('[data-as-configured-submit]');

    if (mainPrice) mainPrice.textContent = money(basePrice);

    let addonTotal = 0;

    if (addonRows) {
      addonRows.innerHTML = '';

      addons.forEach((addon) => {
        const lineTotal = addon.price * addonQuantity;
        addonTotal += lineTotal;

        const row = document.createElement('div');
        row.className = 'autostyle-addons-summary__row';

        const name = document.createElement('span');
        name.textContent = addonQuantity > 1
          ? `${addon.title} × ${addonQuantity}`
          : addon.title;

        const price = document.createElement('strong');
        price.textContent = `+${money(lineTotal)}`;

        row.append(name, price);
        addonRows.appendChild(row);
      });
    }

    const payableToday = basePrice + addonTotal;

    if (total) total.textContent = money(payableToday);

    updateMainDisplayedPrice(root, payableToday);
    updateFinanceBlock(root, payableToday);

    if (button) {
      const available = variant ? variant.available !== false : true;
      const customButtonText =
        String(button.dataset.asButtonText || 'Add configuration to cart').trim() ||
        'Add configuration to cart';
      const showButtonPrice = button.dataset.asShowPrice !== 'false';

      const label = available
        ? (showButtonPrice
            ? `${customButtonText} — ${money(payableToday)}`
            : customButtonText)
        : 'Sold out';

      button.textContent = label;
      button.disabled = !available;
      button.setAttribute('data-autostyle-configuration-total', String(payableToday));
      button.setAttribute('aria-label', label);
    }

    suppressAcceleratedCheckout(root);
    syncInstallationEstimate(checkout);
    dispatchChange(root, addons, payableToday);
  }

  function dispatchChange(root, addons, payableToday) {
    document.dispatchEvent(new CustomEvent('autostyle:addons:change', {
      detail: {
        productId: String(root.dataset.productId || ''),
        productFormId: String(root.dataset.productFormId || ''),
        addons,
        payableToday,
        installationTotal: addons.reduce(
          (sum, addon) => sum + (Number(addon.installationCost) || 0),
          0
        )
      }
    }));
  }

  function updateRoot(root) {
    suppressAcceleratedCheckout(root);
    updateCompatibility(root);
    updateDependencies(root);
    updateAddonSelectionStates(root);
    updateCheckout(root);
  }

  function initRoot(root) {
    if (!root || root.dataset.asInitialized === 'true') return;

    root.dataset.asInitialized = 'true';
    root._asVariants = parseJSONScript(root, '[data-as-product-variants]');
    updateRoot(root);
    startVariantWatcher(root);
  }

  function initCheckout(checkout) {
    if (!checkout || checkout.dataset.asCheckoutInitialized === 'true') return;

    checkout.dataset.asCheckoutInitialized = 'true';

    const root = rootForCheckout(checkout);
    if (root) updateRoot(root);

    syncInstallationEstimate(checkout);
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
    if (scope.matches?.(CHECKOUT_SELECTOR)) initCheckout(scope);

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
    scope.querySelectorAll?.(CHECKOUT_SELECTOR).forEach(initCheckout);
  }

  function rootsForForm(form) {
    return Array.from(document.querySelectorAll(ROOT_SELECTOR)).filter((root) => {
      return getForm(root) === form;
    });
  }

  function collectLineItemProperties(form) {
    const result = {};
    const data = new FormData(form);

    for (const [name, value] of data.entries()) {
      const match = name.match(/^properties\[(.*)\]$/);
      if (!match) continue;

      const key = match[1];
      const stringValue = String(value ?? '').trim();

      if (key && stringValue) result[key] = stringValue;
    }

    return result;
  }

  function bundleId() {
    const random = Math.random().toString(36).slice(2, 8).toUpperCase();
    return `AS-${Date.now().toString(36).toUpperCase()}-${random}`;
  }

  function setBusy(root, busy) {
    root.classList.toggle('autostyle-addons--busy', busy);
    root.setAttribute('aria-busy', busy ? 'true' : 'false');

    const checkout = checkoutForRoot(root);
    if (checkout) {
      checkout.classList.toggle('autostyle-addons--busy', busy);
      checkout.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
  }

  function showError(root, message) {
    const checkout = checkoutForRoot(root);

    const errorBox =
      checkout?.querySelector('[data-as-checkout-error]') ||
      root.querySelector('[data-as-error]');

    if (!errorBox) return;

    errorBox.textContent = message;
    errorBox.hidden = !message;
  }


  function cartLineRequestsAutostyleInstallation(item) {
    const properties = item?.properties || {};
    const legacyPreference =
      String(properties['Installation preference'] || '').trim();
    const displayPreference =
      String(properties['Installation'] || '').trim();

    return (
      legacyPreference === INSTALLATION_REQUESTED_VALUE ||
      displayPreference === 'Autostyle Installation'
    );
  }

  async function syncInstallationCartAttribute() {
    const rootPath = window.Shopify?.routes?.root || '/';

    const cartResponse = await fetch(`${rootPath}cart.js`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!cartResponse.ok) {
      throw new Error('Unable to verify the installation delivery setting.');
    }

    const cart = await cartResponse.json();
    const installationRequested =
      Array.isArray(cart.items) &&
      cart.items.some(cartLineRequestsAutostyleInstallation);

    const updateResponse = await fetch(`${rootPath}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          [INSTALLATION_CART_ATTRIBUTE]: installationRequested ? 'yes' : 'no'
        }
      })
    });

    if (!updateResponse.ok) {
      throw new Error('Unable to save the installation delivery setting.');
    }

    return installationRequested;
  }

  async function addConfiguredProduct(form, roots) {
    const root = roots[0];
    if (!root) return false;

    roots.forEach(updateRoot);

    if (!form.checkValidity()) {
      form.reportValidity();
      return false;
    }

    const allAddons = roots.flatMap(selectedAddons);
    const data = new FormData(form);
    const mainVariantId = Number(data.get('id'));
    const quantity = Math.max(1, parseInt(data.get('quantity') || '1', 10) || 1);
    const sellingPlan = data.get('selling_plan');
    const matchQuantity = root.dataset.matchMainQuantity !== 'false';
    const addonQuantity = matchQuantity ? quantity : 1;
    const token = bundleId();

    if (!Number.isFinite(mainVariantId) || mainVariantId <= 0) {
      throw new Error('Please select a product option before adding to cart.');
    }

    const mainProperties = collectLineItemProperties(form);

    const installationPreference =
      String(mainProperties['Installation preference'] || '').trim();

    // Replace the internal form wording with a clearer customer-facing
    // property in the cart and Shopify order.
    delete mainProperties['Installation preference'];

    if (installationPreference === INSTALLATION_REQUESTED_VALUE) {
      mainProperties['Installation'] = 'Autostyle Installation';
    } else {
      mainProperties['Installation'] = "I'll arrange installation myself";

      // A self-arranged installation must not carry an Autostyle
      // installation estimate into the cart or order.
      delete mainProperties['Estimated installation price'];
    }

    mainProperties['_Autostyle configuration'] = token;
    mainProperties['_No delivery required'] =
      installationPreference === INSTALLATION_REQUESTED_VALUE
        ? 'yes'
        : 'no';

    const mainItem = {
      id: mainVariantId,
      quantity,
      properties: mainProperties
    };

    if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);

    const items = [mainItem];

    allAddons.forEach((addon) => {
      items.push({
        id: addon.variantId,
        quantity: addonQuantity,
        properties: {
          '_Autostyle configuration': token,
          '_Added with': root.dataset.productTitle || 'Main product'
        }
      });
    });

    const checkout = checkoutForRoot(root);
    const configuredButton = checkout?.querySelector('[data-as-configured-submit]') || null;

    roots.forEach((itemRoot) => {
      setBusy(itemRoot, true);
      showError(itemRoot, '');
    });

    if (configuredButton) {
      configuredButton.dataset.asPreviousText = configuredButton.textContent;
      configuredButton.textContent = 'Adding configuration…';
      configuredButton.disabled = true;
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(
          payload.description ||
          payload.message ||
          'Unable to add this configuration to cart.'
        );
      }

      document.dispatchEvent(new CustomEvent('autostyle:addons:added', {
        detail: { payload, items }
      }));

      await syncInstallationCartAttribute();

      const redirect = root.dataset.redirectToCart !== 'false';

      if (redirect) {
        window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      } else {
        window.location.reload();
      }

      return true;
    } finally {
      roots.forEach((itemRoot) => setBusy(itemRoot, false));

      if (configuredButton) {
        if (configuredButton.dataset.asPreviousText) {
          configuredButton.textContent = configuredButton.dataset.asPreviousText;
          delete configuredButton.dataset.asPreviousText;
        }

        const variant = currentVariant(root, form);
        configuredButton.disabled = variant ? variant.available === false : false;
      }
    }
  }

  async function submitConfiguredForm(form, roots) {
    try {
      return await addConfiguredProduct(form, roots);
    } catch (error) {
      console.error('Autostyle add-ons:', error);

      roots.forEach((root) => {
        setBusy(root, false);
        showError(
          root,
          error.message || 'Unable to add this configuration to cart.'
        );
      });

      return false;
    }
  }

  document.addEventListener('autostyle:addons:change', (event) => {
    const productId = String(event.detail?.productId || '');
    const productFormId = String(event.detail?.productFormId || '');

    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((checkout) => {
      const sameForm =
        productFormId &&
        String(checkout.dataset.productFormId || '') === productFormId;

      const sameProduct =
        productId &&
        String(checkout.dataset.productId || '') === productId;

      if (sameForm || sameProduct) {
        syncInstallationEstimate(checkout);
      }
    });
  });

  document.addEventListener('click', async (event) => {
    const configuredButton = event.target.closest?.('[data-as-configured-submit]');
    if (!configuredButton) return;

    const checkout = configuredButton.closest(CHECKOUT_SELECTOR);
    if (!checkout) return;

    const root = rootForCheckout(checkout);
    if (!root) {
      const errorBox = checkout.querySelector('[data-as-checkout-error]');

      if (errorBox) {
        errorBox.textContent = 'Unable to locate the Autostyle add-on configuration.';
        errorBox.hidden = false;
      }

      return;
    }

    const form = getForm(root);

    if (!form) {
      showError(root, 'Unable to locate the Shopify product form.');
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    await submitConfiguredForm(form, rootsForForm(form));
  }, true);

  document.addEventListener('submit', async (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('form[action*="/cart/add"]')) return;

    const roots = rootsForForm(form);
    if (roots.length === 0) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    await submitConfiguredForm(form, roots);
  }, true);

  document.addEventListener('change', (event) => {
    if (event.target.matches?.('[name="properties[Installation preference]"]')) {
      const form = event.target.closest('form[action*="/cart/add"]');

      if (form) {
        document.querySelectorAll(CHECKOUT_SELECTOR).forEach((checkout) => {
          if (getForm(checkout) === form) {
            syncInstallationEstimate(checkout);
          }
        });
      }
    }

    const checkbox = event.target.closest?.(CHECKBOX_SELECTOR);

    if (checkbox) {
      const root = checkbox.closest(ROOT_SELECTOR);
      if (root) updateRoot(root);
      return;
    }

    const form = event.target.closest?.('form[action*="/cart/add"]');

    if (form) {
      setTimeout(() => rootsForForm(form).forEach(updateRoot), 0);
    }
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches?.('[name="quantity"]')) return;

    const form = event.target.closest('form[action*="/cart/add"]');
    if (form) rootsForForm(form).forEach(updateRoot);
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) initAll(node);
      });
    });

    document.querySelectorAll(ROOT_SELECTOR).forEach(suppressAcceleratedCheckout);
  });

  document.addEventListener('DOMContentLoaded', () => {
    initAll();
    observer.observe(document.body, { childList: true, subtree: true });
  });

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  if (document.readyState !== 'loading') {
    initAll();
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();